import {
  failOperation,
  startOperation,
  succeedOperation,
} from '@mint-lib/datadog';

/**
 * Prompt metadata only: the prompt text and the AI output stay out.
 * There is no Mint-version flag in the AI hub, so none is sent.
 */
export type ChatOperationContext = {
  source: 'input' | 'suggested-prompt';
  fileCount: number;
  redmillUser: boolean;
};

/**
 * The open operation of one chat. `ai.chat.start` is keyed by a per-click id,
 * since the chat id only exists once `createChat` returns. `ai.prompt.send` is
 * keyed by the chat id: the stream frames carry only a per-message id, and the
 * socket that delivers them is opened for one chat session.
 */
type OpenChatOperation = { kind: 'chat-start' | 'prompt-send'; key: string };

// Module state rather than React state: the AI hub pages start the
// operations, the new-chat page unmounts before the answer streams, and the
// chat socket in `useTextChatActions` ends them.
/** The open operation of each chat, by chat id. */
const openByChat = new Map<string, OpenChatOperation>();
/** Chats whose last operation failed, so the next prompt there is a retry. */
const failedChats = new Set<string>();
let lastChatStartFailed = false;

const succeed = ({ kind, key }: OpenChatOperation) => {
  if (kind === 'chat-start') {
    succeedOperation('ai.chat.start', { key });
  } else {
    succeedOperation('ai.prompt.send', { key });
  }
};

const fail = (
  { kind, key }: OpenChatOperation,
  reason: 'error' | 'abandoned',
) => {
  if (kind === 'chat-start') {
    failOperation('ai.chat.start', reason, { key });
  } else {
    failOperation('ai.prompt.send', reason, { key });
  }
};

/** Holds `operation` for `chatId`. One still open there is abandoned first. */
const open = (chatId: string, operation: OpenChatOperation) => {
  const previous = openByChat.get(chatId);
  if (previous) {
    fail(previous, 'abandoned');
  }
  openByChat.set(chatId, operation);
};

/**
 * Starts `ai.chat.start` when the first message of a new chat is sent. Call
 * `created` with the chat id `createChat` returns, so the chat's stream can end
 * it, or `failed` when `createChat` fails. A start after a failed one is
 * described as `retry`.
 */
export function startChatOperation(context: ChatOperationContext) {
  const key = crypto.randomUUID();
  startOperation('ai.chat.start', {
    key,
    context,
    description: lastChatStartFailed ? 'retry' : undefined,
  });

  return {
    created: (chatId: string) => {
      open(chatId, { kind: 'chat-start', key });
    },
    failed: () => {
      lastChatStartFailed = true;
      failOperation('ai.chat.start', 'error', { key });
    },
  };
}

/**
 * Starts `ai.prompt.send` when a prompt is sent in an existing chat. A prompt
 * after a failed one in the same chat is described as `retry`.
 */
export function startPromptOperation(
  chatId: string,
  context: ChatOperationContext,
) {
  open(chatId, { kind: 'prompt-send', key: chatId });
  startOperation('ai.prompt.send', {
    key: chatId,
    context,
    description: failedChats.has(chatId) ? 'retry' : undefined,
  });
}

/** Call when the last AI message of an answer (`isLast`) arrives. */
export function succeedChatOperation(chatId: string) {
  const operation = openByChat.get(chatId);
  if (!operation) return;
  openByChat.delete(chatId);
  failedChats.delete(chatId);
  if (operation.kind === 'chat-start') {
    lastChatStartFailed = false;
  }
  succeed(operation);
}

/**
 * Fails the operation open in `chatId`: `error` on a socket error or
 * disconnect, `abandoned` on interrupt or when the chat is left mid-answer.
 */
export function failChatOperation(
  chatId: string,
  reason: 'error' | 'abandoned',
) {
  const operation = openByChat.get(chatId);
  if (!operation) return;
  openByChat.delete(chatId);
  if (reason === 'error') {
    failedChats.add(chatId);
  }
  fail(operation, reason);
}
